import { create } from "zustand";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { AccessLevel } from "./billingCodes";

// The billing status of each organization the signed-in user belongs to, as the
// API last reported it.
//
// It is what the app decides by whether an organization is let in (accessLevel
// "full") or held on the billing page ("billing_only"), so it is kept in
// localStorage beside the rest of the sign-in: a reload lands on the right side
// of that line without waiting for a round trip. Everything that reports a
// status writes it here — the sign-in responses, which embed one per
// organization, every billing call that returns one, and the 402 gate, which
// says an organization has just been held.
//
// Nothing here is authoritative. The server decides every request on its own,
// and the billing page re-reads the status from Stripe as it opens.
//
// The two can disagree: the gate can go on refusing an organization whose
// status says it is let in — a payment Stripe has taken but the gate has not
// heard of yet, a billing-status that no longer answers at all. A status read
// never lifts a 402 on its own, then, or the billing page would hand the
// organization back to the app, the gate would send it straight back, and the
// two would trade it for as long as they disagree. The hold lasts until a status
// agrees the organization is held (and the page's fixes take over), the user
// asks to try again, or a sign in starts everything afresh.
//
// An organization with no entry is one the backend reported no billing status
// for: the new billing flow is switched off for it (Billing:NewFlow), and the
// screens it reaches keep to the flow that predates it.

export const BILLING_STATUS_STORAGE_KEY = "billingStatuses";

const readStored = () => {
   const stored = LocalStorageService.getItem(BILLING_STATUS_STORAGE_KEY);
   return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
};

const persist = (statuses) => {
   if (Object.keys(statuses).length) {
      LocalStorageService.setItem(BILLING_STATUS_STORAGE_KEY, statuses);
   } else {
      LocalStorageService.removeItem(BILLING_STATUS_STORAGE_KEY);
   }
};

const CAN_MANAGE_BILLING_STORAGE_KEY = "canManageBilling";

const persistCanManageBilling = (value) => {
   if (value) {
      LocalStorageService.setItem(CAN_MANAGE_BILLING_STORAGE_KEY, true);
   } else {
      LocalStorageService.removeItem(CAN_MANAGE_BILLING_STORAGE_KEY);
   }
};

const without = (map, key) => {
   if (!(key in map)) return map;

   const { [key]: _dropped, ...rest } = map;
   return rest;
};

export const useBillingStore = create((set, get) => ({
   statuses: readStored(),

   /**
    * The organizations a 402 is holding against a status that let them in, and
    * that no status has agreed to since (see above). Not kept across a reload:
    * the gate says it again on the first call.
    */
   gateHolds: {},

   /**
    * Whether the signed-in user may manage the selected organization's billing,
    * as selectOrganization reports it (`canManageBilling`): the owner, the
    * Organization and OrgSuperAdmin roles, and any role with the ManagePayment
    * permission, the same check every /Payment/* call is answered by. Kept
    * across a reload like the rest of the selected organization; false until
    * the server says otherwise.
    */
   canManageBilling: LocalStorageService.getItem(CAN_MANAGE_BILLING_STORAGE_KEY) === true,

   setCanManageBilling: (value) => {
      const canManageBilling = value === true;
      if (canManageBilling === get().canManageBilling) return;

      persistCanManageBilling(canManageBilling);
      set({ canManageBilling });
   },

   /**
    * What selectOrganization said about each organization it was asked for
    * (`canManageBilling`), selected or not: an organization the user was
    * turned away from is never selected, but the organization list still has
    * to show it locked. Not kept across a reload, and emptied by a sign in.
    */
   billingRights: {},

   rememberBillingRight: (organizationId, value) => {
      if (!organizationId || typeof value !== "boolean") return;
      if (get().billingRights[organizationId] === value) return;

      set({ billingRights: { ...get().billingRights, [organizationId]: value } });
   },

   /** Every organization's status from a sign-in response, in place of what was held. */
   replaceStatuses: (statuses) => {
      const next = statuses ?? {};
      persist(next);
      set({ statuses: next, gateHolds: {}, billingRights: {} });
   },

   /**
    * One organization's status, as a billing call or a sign-in response
    * reported it. One that holds the organization agrees with the gate, which
    * then has nothing left to hold it by: a fix that lets it in lets it in.
    */
   setStatus: (organizationId, status) => {
      if (!organizationId || !status) return;

      const statuses = { ...get().statuses, [organizationId]: status };
      persist(statuses);
      set({
         statuses,
         gateHolds: isBillingOnly(status) ? without(get().gateHolds, organizationId) : get().gateHolds
      });
   },

   /**
    * A 402: the organization is held at billing_only, for `reason`. Whatever
    * else was known about it stays until the billing page re-reads the whole
    * status.
    *
    * Against a status that let it in, the 402 also holds it until a status
    * agrees or the hold is released. Against one that already held it there is
    * nothing to disagree about, and nothing is kept that would stand in the
    * way of the fix that lets it in.
    */
   markBillingRequired: (organizationId, reason) => {
      if (!organizationId) return;

      const previous = get().statuses[organizationId] ?? {};
      const statuses = {
         ...get().statuses,
         [organizationId]: {
            ...previous,
            accessLevel: AccessLevel.BILLING_ONLY,
            reason: reason ?? previous.reason ?? null
         }
      };
      persist(statuses);
      set({
         statuses,
         gateHolds: isBillingOnly(previous)
            ? get().gateHolds
            : { ...get().gateHolds, [organizationId]: true }
      });
   },

   /** Let the app try the organization again, past a 402 no status agreed with. */
   releaseGateHold: (organizationId) => {
      set({ gateHolds: without(get().gateHolds, organizationId) });
   },

   /**
    * Drop one organization — the backend no longer reports a status for it. A
    * 402 hold stays: the gate is still refusing it.
    */
   forget: (organizationId) => {
      if (!organizationId || !(organizationId in get().statuses)) return;

      const rest = without(get().statuses, organizationId);
      persist(rest);
      set({ statuses: rest });
   },

   clear: () => {
      persist({});
      persistCanManageBilling(false);
      set({ statuses: {}, gateHolds: {}, canManageBilling: false, billingRights: {} });
   }
}));

/** The stored status of one organization, or null. */
export const getStoredBillingStatus = (organizationId) =>
   organizationId ? useBillingStore.getState().statuses[organizationId] ?? null : null;

/** True when a status holds its organization on the billing page. */
export const isBillingOnly = (status) => status?.accessLevel === AccessLevel.BILLING_ONLY;

/** True while a 402 holds the organization that no status has agreed to since. */
export const isHeldByGate = (organizationId) =>
   Boolean(organizationId && useBillingStore.getState().gateHolds[organizationId]);

/**
 * True when the backend reports billing statuses for this organization — the
 * new billing flow is on for it, and its screens use the new endpoints.
 */
export const isBillingManaged = (organizationId) => Boolean(getStoredBillingStatus(organizationId));
